export enum DeviceType {
  MOBILE = "Mobile",
  MOBILE_OR_SMALL_COMPUTER = "Mobile or small computer",
  TABLET = "Tablet or Small Laptop",
  LAPTOP = "Laptop or Small Desktop",
  DESKTOP = "Desktop",
}

/**
 * Detects the device type based on the user agent and screen size
 * Note: This detection is primarily width-based, so "Tablet or Small Laptop"
 * and "Laptop or Small Desktop" categories reflect screen width ranges rather
 * than actual device types.
 * @returns DeviceType - Mobile, Mobile or small computer, Tablet or Small Laptop, Laptop or Small Desktop, or Desktop
 */
export function detectDeviceType(): DeviceType {
  const userAgent =
    navigator.userAgent ||
    navigator.vendor ||
    (window as Window & typeof globalThis & { opera?: string }).opera ||
    "";

  // Check for specific mobile devices
  const isMobileUA =
    /Android.*Mobile|iPhone|iPod|BlackBerry|IEMobile|Opera Mini|Windows Phone/i.test(
      userAgent,
    );

  // Check for tablets (including iPad)
  const isTabletUA =
    /iPad|Android(?!.*Mobile)|Tablet|PlayBook|Kindle|Silk/i.test(userAgent);

  // Get screen dimensions
  const screenWidth =
    window.innerWidth ||
    document.documentElement.clientWidth ||
    document.body.clientWidth;

  // Determine device type
  if (isMobileUA) {
    // User agent specifically indicates a mobile device
    return DeviceType.MOBILE;
  } else if (!isMobileUA && screenWidth < 768) {
    // Screen width is small but UA doesn't say mobile - could be a small laptop/computer
    return DeviceType.MOBILE_OR_SMALL_COMPUTER;
  } else if (isTabletUA || (screenWidth >= 768 && screenWidth <= 1024)) {
    return DeviceType.TABLET;
  } else {
    // For desktop/laptop distinction, use synchronous heuristics
    const hasTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    const hasBatteryAPI = "getBattery" in navigator;

    // If it has battery API available, more likely a laptop
    // If it's a touchscreen device in this size range, more likely a laptop
    // Screen width between 1024-1920 is typical for laptops
    if (
      hasBatteryAPI ||
      (hasTouch && screenWidth >= 1024 && screenWidth <= 1920) ||
      (screenWidth >= 1280 && screenWidth <= 1920)
    ) {
      return DeviceType.LAPTOP;
    } else {
      return DeviceType.DESKTOP;
    }
  }
}

/**
 * Detects browser information from user agent
 */
export function detectBrowser() {
  const userAgent = navigator.userAgent;

  // Detect browser name and version
  let browserName = "Other";
  let browserVersion = "Unknown";

  if (userAgent.indexOf("Firefox") > -1) {
    browserName = "Firefox";
    const match = userAgent.match(/Firefox\/(\d+)/);
    if (match) browserVersion = match[1];
  } else if (userAgent.indexOf("Edg") > -1) {
    browserName = "Edge";
    const match = userAgent.match(/Edg\/(\d+)/);
    if (match) browserVersion = match[1];
  } else if (userAgent.indexOf("Chrome") > -1) {
    browserName = "Chrome";
    const match = userAgent.match(/Chrome\/(\d+)/);
    if (match) browserVersion = match[1];
  } else if (userAgent.indexOf("Safari") > -1) {
    browserName = "Safari";
    const match = userAgent.match(/Version\/(\d+)/);
    if (match) browserVersion = match[1];
  } else if (
    userAgent.indexOf("MSIE") > -1 ||
    userAgent.indexOf("Trident") > -1
  ) {
    browserName = "Internet Explorer";
    const match = userAgent.match(/(?:MSIE |rv:)(\d+)/);
    if (match) browserVersion = match[1];
  }

  return {
    name: browserName,
    version: browserVersion,
  };
}

/**
 * Gets additional device information for analytics
 */
export function getDeviceInfo() {
  const browser = detectBrowser();

  return {
    deviceType: detectDeviceType(),
    screenWidth: window.innerWidth,
    screenHeight: window.innerHeight,
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    browser: `${browser.name} ${browser.version}`,
    browserName: browser.name,
    browserVersion: browser.version,
  };
}
